import {useCallback, useEffect, useState} from "react";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {
    getEmpresa,
    getHistorico,
    getLicenca,
    getOpcionais,
    getPreviaDaLicenca,
    getVersaoDoPlano,
    listModulosDoSistema
} from "./LicencasService";

const dataDe = (response) => response?.data?.data ?? null;
const valorDe = (response) => response?.data?.valor ?? null;
const resultado = (settled, ler) => (settled.status === 'fulfilled' ? ler(settled.value) : null);

const mensagemDaPrevia = (error) => (error?.response?.status === 502
    ? 'A prévia da fatura está indisponível no momento.'
    : getBackendErrorMessage(error, 'Não foi possível calcular a prévia da fatura.'));

/*
 * Tudo o que a página de uma assinatura mostra. A licença vem do app-licencas; a empresa (dia de fechamento), do
 * app-empresas; a versão e os módulos, do app-funcionalidades; a prévia, do faturamento. Só a licença é obrigatória:
 * o resto que falhar fica vazio, e a página diz o que faltou.
 */
export const useAssinatura = (id) => {
    const [estado, setEstado] = useState({
        carregando: true,
        erro: '',
        licenca: null,
        historico: null,
        empresa: null,
        versao: null,
        opcionais: null,
        modulos: [],
        previa: {valor: null, erro: ''},
    });

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, carregando: true, erro: ''}));
        let licenca;
        try {
            licenca = dataDe(await getLicenca(id));
        } catch (error) {
            setEstado((atual) => ({...atual, carregando: false, erro: getBackendErrorMessage(error, 'Não foi possível carregar a assinatura.')}));
            return;
        }
        const sistemaId = licenca?.plano?.id_sistema || licenca?.plano?.sistema?.id;
        const [historico, empresa, versao, opcionais, modulos, previa] = await Promise.allSettled([
            getHistorico(id),
            getEmpresa(licenca.id_empresa),
            licenca.versao ? getVersaoDoPlano(licenca.id_plano, licenca.versao.numero) : Promise.resolve(null),
            getOpcionais(id),
            sistemaId ? listModulosDoSistema(sistemaId) : Promise.resolve(null),
            getPreviaDaLicenca(id),
        ]);
        setEstado({
            carregando: false,
            erro: '',
            licenca,
            historico: resultado(historico, dataDe),
            empresa: resultado(empresa, valorDe),
            versao: resultado(versao, valorDe),
            opcionais: resultado(opcionais, dataDe),
            modulos: resultado(modulos, (response) => valorDe(response)?.items) || [],
            previa: previa.status === 'fulfilled'
                ? {valor: dataDe(previa.value), erro: ''}
                : {valor: null, erro: mensagemDaPrevia(previa.reason)},
        });
    }, [id]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // Muda na tela o que uma ação já garantiu, sem esperar o `recarregar`.
    const ajustarLicenca = useCallback((ajuste) => {
        setEstado((atual) => (atual.licenca ? {...atual, licenca: ajuste(atual.licenca)} : atual));
    }, []);

    return {...estado, recarregar: carregar, ajustarLicenca};
};
